import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Download, Eye, EyeOff } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../i18n/useTranslation';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import { Section } from '../components/templates/Section/Section';
import { Alert } from '../components/atoms/Alert/Alert';
import { Badge } from '../components/atoms/Badge/Badge';
import { Button } from '../components/atoms/Button/Button';
import { Card } from '../components/atoms/Card/Card';
import { Checkbox } from '../components/atoms/Checkbox';
import { EmptyState } from '../components/atoms/EmptyState';
import { Grid } from '../components/atoms/Grid/Grid';
import { Inline } from '../components/atoms/Inline/Inline';
import { Input } from '../components/atoms/Input/Input';
import { LiveRegion } from '../components/atoms/LiveRegion/LiveRegion';
import { Select } from '../components/atoms/Select/Select';
import { Stack } from '../components/atoms/Stack/Stack';
import { Text } from '../components/atoms/Typography/Text';
import { Textarea } from '../components/atoms/Textarea/Textarea';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';
import { Dialog } from '../components/ui/Dialog/Dialog';
import { FlowMapLegend, FlowMapPhoto } from '../components/flow/FlowMapPhoto';
import { FlowPlacementEditor, type FlowEditTarget, type FlowEditorMode } from '../components/flow/FlowPlacementEditor';
import { FlowCoralList } from '../components/flow/FlowCoralList';
import { FlowEvaluationCard } from '../components/flow/FlowEvaluationCard';
import { FlowDayView } from '../components/flow/FlowDayView';
import { CopySurveyForm } from '../components/flow/CopySurveyForm';
import { listLivestock } from '../services/livestockService';
import { useAquariums } from '../hooks/useAquariums';
import { invalidateAiUsage } from '../hooks/useAiUsage';
import { formatSurveyDate, pointLabel, saveBlob, type LightGroup } from '../services/lightSurveyService';
import {
  addFlowCoral,
  addFlowPoint,
  addPump,
  deleteFlowItem,
  deleteFlowSurvey,
  downloadFlowOverlay,
  clockOf,
  equipmentPumpOptions,
  estimateFlowFromVideo,
  flowEvaluationKey,
  minutesOf,
  flowSurveyKey,
  flowSurveysKey,
  formatRange,
  getFlowSurvey,
  updateFlowCoral,
  updateFlowPoint,
  updateFlowSurvey,
  updatePump,
  PUMP_FACINGS,
  type FlowPointInput,
  type FlowSurveyDetail,
  type FlowView,
} from '../services/flowSurveyService';
import { invalidateSharedCorals } from '../lib/sharedCorals';
import '../components/lighting/Lighting.css';
import '../components/flow/FlowMap.css';

/** Name and notes of a survey; delete it. */
function SurveyDetailsForm({ survey, onSaved, onDelete }: { survey: FlowSurveyDetail; onSaved: (s: FlowSurveyDetail) => void; onDelete: () => void }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(survey.title);
  const [notes, setNotes] = useState(survey.notes ?? '');
  const [clock, setClock] = useState(survey.measuredClock ?? '');
  const [error, setError] = useState<string | undefined>(undefined);
  const save = useMutation({
    mutationFn: () => updateFlowSurvey(survey.aquariumId, survey.id, { title: title.trim(), measured_clock: clock || null, notes: notes.trim() || null }),
    onSuccess: onSaved,
  });
  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const invalid = title.trim() === '';
    setError(invalid ? String(t('flow.new.titleRequired')) : undefined);
    if (!invalid) save.mutate();
  };
  return (
    <form onSubmit={submit} noValidate>
      <Stack gap="4">
        <Input id="flow-survey-title-edit" label={t('flow.new.title')} value={title} onChange={(e) => setTitle(e.target.value)} error={error} maxLength={120} fullWidth />
        <Input id="flow-survey-time-edit" type="time" label={t('flow.new.time')} helperText={t('flow.new.timeHint')} value={clock} onChange={(e) => setClock(e.target.value)} />
        <Textarea id="flow-survey-notes" label={t('flow.details.notes')} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={1000} />
        {save.isError && <Alert compact>{t('flow.details.error')}</Alert>}
        {save.isSuccess && <Alert tone="success" live="polite" compact>{t('flow.details.saved')}</Alert>}
        <Inline gap="2" justify="between">
          <Button type="submit" variant="secondary" isLoading={save.isPending}>{t('flow.details.save')}</Button>
          <Button type="button" variant="danger" onClick={onDelete}>{t('flow.details.delete')}</Button>
        </Inline>
      </Stack>
    </form>
  );
}

/**
 * One flow survey ("Strömungskarte"): the photo with pumps, flow readings
 * (ranges, peak or trough) and the placed corals; adding them; the turnover;
 * whether each coral sits in the flow its group needs; and where a new coral of
 * a group would fit. Every write returns the recomputed survey.
 */
export function FlowSurveyPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { aquariumId = '', surveyId = '' } = useParams<{ aquariumId: string; surveyId: string }>();
  const [mode, setMode] = useState<FlowEditorMode>('point');
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [editing, setEditing] = useState<FlowEditTarget | null>(null);
  const [view, setView] = useState<FlowView>('peak');
  const [showGrid, setShowGrid] = useState(true);
  const [showMeasurements, setShowMeasurements] = useState(true);
  const [finderGroup, setFinderGroup] = useState<LightGroup | ''>('');
  const [announcement, setAnnouncement] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Time of day shown on the map: null = as measured, else estimated from the pump schedules.
  const [at, setAt] = useState<string | null>(null);
  // The re-measurement dialog: open with the prefilled time (null = no suggestion).
  const [copyAt, setCopyAt] = useState<{ clock: string | null } | null>(null);
  // Bumped after every successful save: a fresh, empty form (kept as typed on a failure).
  const [saved, setSaved] = useState(0);

  const key = flowSurveyKey(aquariumId, surveyId);
  const query = useQuery({
    queryKey: [...key, at],
    queryFn: () => getFlowSurvey(aquariumId, surveyId, at ?? undefined),
    enabled: Boolean(aquariumId && surveyId),
    placeholderData: (previous) => previous,
  });
  const aquariums = useAquariums();
  const pumpsFromEquipment = useMemo(() => equipmentPumpOptions(aquariums.data?.find((a) => a.id === aquariumId)), [aquariums.data, aquariumId]);
  const livestock = useQuery({
    queryKey: ['livestock', aquariumId, 'flow-corals'],
    queryFn: () => listLivestock(aquariumId, { category: 'coral' }),
    enabled: Boolean(aquariumId),
  });

  const applied = (survey: FlowSurveyDetail, message: string): void => {
    queryClient.setQueryData([...key, null], survey);
    // Estimated views, the list and the evaluation's `outdated` flag depend on the survey state.
    void queryClient.invalidateQueries({ queryKey: key });
    void queryClient.invalidateQueries({ queryKey: flowSurveysKey(aquariumId), exact: true });
    void queryClient.invalidateQueries({ queryKey: flowEvaluationKey(aquariumId, surveyId) });
    setAnnouncement(message);
  };
  const afterSave = (survey: FlowSurveyDetail, message: string): void => {
    applied(survey, message);
    setEditing(null);
    setPosition(null);
    setSaved((n) => n + 1);
  };

  const savePoint = useMutation({
    mutationFn: ({ input, id }: { input: FlowPointInput; id: string | null }) =>
      id ? updateFlowPoint(aquariumId, surveyId, id, input) : addFlowPoint(aquariumId, surveyId, input),
    onSuccess: (survey, { id }) => afterSave(survey, String(t(id ? 'flow.announce.pointSaved' : 'flow.announce.pointAdded'))),
  });
  const savePump = useMutation({
    mutationFn: ({ input, id }: { input: Parameters<typeof addPump>[2]; id: string | null }) =>
      id ? updatePump(aquariumId, surveyId, id, input) : addPump(aquariumId, surveyId, input),
    onSuccess: (survey, { input, id }) => {
      afterSave(survey, String(t(id ? 'flow.announce.pumpSaved' : 'flow.announce.pumpAdded')));
      // A new pump was added to the aquarium's equipment too.
      if (input.equipment) void queryClient.invalidateQueries({ queryKey: ['aquariums'] });
    },
  });
  const saveCoral = useMutation({
    mutationFn: ({ input, id }: { input: Parameters<typeof addFlowCoral>[2]; id: string | null }) =>
      id ? updateFlowCoral(aquariumId, surveyId, id, input) : addFlowCoral(aquariumId, surveyId, input),
    onSuccess: (survey, { id }) => {
      afterSave(survey, String(t(id ? 'flow.announce.coralSaved' : 'flow.announce.coralAdded')));
      invalidateSharedCorals(queryClient, aquariumId);
    },
  });
  const remove = useMutation({
    mutationFn: ({ kind, id }: { kind: 'pumps' | 'points' | 'corals'; id: string }) => deleteFlowItem(aquariumId, surveyId, kind, id),
    onSuccess: (survey, { kind }) => {
      applied(survey, String(t('flow.announce.removed')));
      if (kind === 'corals') invalidateSharedCorals(queryClient, aquariumId);
    },
  });
  const exportImage = useMutation({
    mutationFn: (survey: FlowSurveyDetail) => downloadFlowOverlay(aquariumId, survey, { estimate: showGrid, view }),
    onSuccess: ({ blob, filename }) => {
      saveBlob(blob, filename);
      setAnnouncement(String(t('flow.map.exported')));
    },
  });
  const removeSurvey = useMutation({
    mutationFn: () => deleteFlowSurvey(aquariumId, surveyId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: flowSurveysKey(aquariumId) });
      navigate(`/aquariums/${encodeURIComponent(aquariumId)}/flow`);
    },
  });

  const survey = query.data;
  const assessment = survey?.assessment;
  const levelsOf = useMemo(() => new Map((assessment?.levels ?? []).map((l) => [l.id, { levelMin: l.levelMin, levelMax: l.levelMax }])), [assessment]);
  const shownClock = assessment?.at ?? survey?.measuredClock ?? '12:00';
  const flagOf = useMemo(() => new Map((assessment?.points ?? []).map((p) => [p.id, p.flag])), [assessment]);
  const statusOf = useMemo(() => new Map((assessment?.corals ?? []).map((a) => [a.coralId, a.status])), [assessment]);
  const spots = finderGroup ? assessment?.spots.find((s) => s.group === finderGroup) : undefined;
  const highlightIds = spots ? new Set(spots.pointIds) : null;
  const busy = savePoint.isPending || savePump.isPending || saveCoral.isPending || remove.isPending;
  const backLink = (
    <Link className="lighting__back-link" to={`/aquariums/${encodeURIComponent(aquariumId)}/flow`}>
      {t('flow.detail.back')}
    </Link>
  );

  const startEdit = (target: FlowEditTarget): void => {
    setEditing(target);
    setMode(target.kind);
    setPosition({ x: target.x, y: target.y });
  };
  const startAdd = (next: FlowEditorMode): void => {
    setEditing(null);
    setMode(next);
  };

  let content;
  if (query.isPending) {
    content = <LiveRegion busy><Text color="muted">{t('flow.loading')}</Text></LiveRegion>;
  } else if (query.isError || !survey || !assessment) {
    content = <LoadErrorState title={t('flow.detail.loadError')} onRetry={() => void query.refetch()} retryLabel={t('flow.retry')} titleLevel={2} />;
  } else {
    const aspect = survey.imageWidth && survey.imageHeight ? survey.imageWidth / survey.imageHeight : 4 / 3;
    const counts = assessment.corals.reduce<Record<string, number>>((acc, a) => ({ ...acc, [a.status]: (acc[a.status] ?? 0) + 1 }), {});
    const turnover = assessment.turnover;
    content = (
      <>
        <Section title={t('flow.map.title')} description={t('flow.map.description')}>
          <Stack gap="4">
            {!survey.imageUrl && <Alert tone="info" live="off" compact>{t('flow.map.noPhoto')}</Alert>}
            <Card>
              <Stack gap="4">
                <div className="par-map__toolbar">
                  <Inline gap="2" role="group" aria-label={t('flow.map.viewLabel')}>
                    {(['peak', 'trough'] as const).map((v) => (
                      <Button key={v} type="button" size="sm" variant={view === v ? 'primary' : 'secondary'} aria-pressed={view === v} onClick={() => setView(v)} data-testid={`flow-view-${v}`}>
                        {t(`flow.map.view.${v}`)}
                      </Button>
                    ))}
                  </Inline>
                  <Inline gap="2">
                    <Button type="button" variant="secondary" size="sm" aria-pressed={showMeasurements} onClick={() => setShowMeasurements((v) => !v)}>
                      {showMeasurements ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
                      {t('flow.map.showMeasurements')}
                    </Button>
                    {survey.points.length + survey.corals.length + survey.pumps.length > 0 && (
                      <Button type="button" variant="ghost" size="sm" isLoading={exportImage.isPending} onClick={() => exportImage.mutate(survey)}>
                        <Download size={16} aria-hidden="true" />
                        {t('flow.map.export')}
                      </Button>
                    )}
                  </Inline>
                </div>
                <Text color="secondary" size="sm">
                  {t('flow.map.counts', { pumps: survey.pumps.length, points: survey.points.length, corals: survey.corals.length })}
                  {` · ${String(t(`flow.map.viewNote.${view}`))}`}
                </Text>
                {exportImage.isError && <Alert compact>{t('flow.map.exportError')}</Alert>}
                {assessment.scheduledPumpIds.length > 0 && (
                  <div className="flow-map__time">
                    <Input
                      id="flow-time"
                      type="range"
                      min={0}
                      max={47}
                      step={1}
                      label={t('flow.time.label', { time: shownClock })}
                      aria-valuetext={shownClock}
                      value={Math.floor(minutesOf(shownClock) / 30)}
                      onChange={(e) => {
                        const next = clockOf(Number(e.target.value) * 30);
                        setAt(next === survey.measuredClock ? null : next);
                      }}
                      fullWidth
                    />
                    {assessment.estimated ? (
                      <Inline gap="2" align="center" justify="between">
                        <Alert tone="info" live="polite" compact data-testid="flow-time-estimated">
                          {t('flow.time.estimated', { time: assessment.at ?? '', measured: survey.measuredClock ?? t('flow.time.measuredUnknown') })}
                        </Alert>
                        <Button type="button" variant="ghost" size="sm" onClick={() => setAt(null)}>{t('flow.time.reset')}</Button>
                      </Inline>
                    ) : (
                      <Text size="sm" color="secondary">{t('flow.time.measured')}</Text>
                    )}
                  </div>
                )}
                <FlowMapPhoto
                  imageUrl={survey.imageUrl}
                  aspect={aspect}
                  pumps={survey.pumps}
                  points={survey.points.map((p) => ({ ...p, ...levelsOf.get(p.id), flag: flagOf.get(p.id) ?? null }))}
                  corals={survey.corals.map((c, i) => ({ id: c.id, number: i + 1, x: c.x, y: c.y, status: statusOf.get(c.id) ?? 'no_data' }))}
                  grid={assessment.grid[view]}
                  showGrid={showGrid}
                  showMeasurements={showMeasurements}
                  highlightIds={highlightIds}
                  pending={position}
                  onPick={(x, y) => setPosition({ x, y })}
                />
                <Inline gap="4" justify="between" align="center">
                  <FlowMapLegend facings={PUMP_FACINGS.filter((f) => survey.pumps.some((p) => p.outlets.includes(f)))} />
                  <Checkbox label={t('flow.map.showEstimate')} checked={showGrid} disabled={!showMeasurements} onChange={(e) => setShowGrid(e.target.checked)} />
                </Inline>
                <Text color="muted" variant="caption">{t('flow.map.estimateNote')}</Text>
              </Stack>
            </Card>
            <Card>
              <FlowPlacementEditor
                // Remounted once the aquarium's pumps are known, so the first one is preselected.
                key={`${editing?.id ?? 'new'}-${saved}-${pumpsFromEquipment.length}`}
                mode={mode}
                onModeChange={setMode}
                position={position}
                onPositionChange={setPosition}
                editing={editing}
                onCancelEdit={() => setEditing(null)}
                corals={(livestock.data ?? []).map((c) => ({ id: c.id, name: c.commonName }))}
                equipmentPumps={pumpsFromEquipment}
                pending={savePoint.isPending || savePump.isPending || saveCoral.isPending}
                failed={savePoint.isError || savePump.isError || saveCoral.isError}
                onSavePoint={(input, id) => savePoint.mutate({ input, id })}
                onSavePump={(input, id) => savePump.mutate({ input, id })}
                onSaveCoral={(input, id) => saveCoral.mutate({ input, id })}
                estimateVideo={async (frames, durationSeconds, pos) => {
                  const estimate = await estimateFlowFromVideo(aquariumId, surveyId, frames, { durationSeconds, x: pos?.x ?? null, y: pos?.y ?? null });
                  invalidateAiUsage(queryClient);
                  return estimate;
                }}
              />
            </Card>
          </Stack>
        </Section>

        {survey.pumps.length > 0 && (
          <Section title={t('flow.day.title')} description={t('flow.day.description')}>
            <FlowDayView
              assessment={assessment}
              corals={survey.corals}
              measuredClock={survey.measuredClock}
              onShow={(clock) => setAt(clock === survey.measuredClock ? null : clock)}
              onMeasureAgain={(clock) => setCopyAt({ clock })}
              onOpenSchedules={() => navigate(`/aquariums/${encodeURIComponent(aquariumId)}/flow`)}
            />
          </Section>
        )}

        <Section title={t('flow.overview.title')} description={t('flow.overview.description')}>
          <Grid minItemWidth="sm" gap="4">
            <Card>
              <Stack gap="2" data-testid="flow-turnover">
                <Text weight="medium">{t('flow.overview.turnover')}</Text>
                {turnover.peak !== null && turnover.trough !== null ? (
                  <>
                    <span className="flow-map__stat-value">{t('flow.overview.turnoverValue', { trough: turnover.trough, peak: turnover.peak })}</span>
                    <Text size="sm" color="secondary">
                      {t('flow.overview.turnoverFlow', { trough: turnover.troughFlowLph, peak: turnover.peakFlowLph, volume: turnover.volumeLiters ?? 0 })}
                    </Text>
                  </>
                ) : (
                  <Text size="sm">{t(turnover.volumeLiters === null ? 'flow.overview.noVolume' : 'flow.overview.noPumpFlow')}</Text>
                )}
                {turnover.pumpsWithoutFlow > 0 && turnover.peak !== null && <Text size="sm">{t('flow.overview.pumpsWithoutFlow', { n: turnover.pumpsWithoutFlow })}</Text>}
                <Text color="muted" variant="caption">
                  {t('flow.overview.targets', { softMin: turnover.targets.softLps[0], softMax: turnover.targets.softLps[1], spsMin: turnover.targets.sps[0], spsMax: turnover.targets.sps[1] })}
                </Text>
              </Stack>
            </Card>
            <Card>
              <Stack gap="2" data-testid="flow-spots">
                <Text weight="medium">{t('flow.overview.spots')}</Text>
                {survey.points.length === 0 ? (
                  <Text size="sm">{t('flow.overview.noReadings')}</Text>
                ) : (
                  <>
                    <Text size="sm">{t('flow.overview.deadSpots', { n: assessment.deadSpotCount })}</Text>
                    <Text size="sm">{t('flow.overview.blasting', { n: assessment.blastingCount })}</Text>
                    {assessment.deadSpotCount > 0 && <Text color="muted" variant="caption">{t('flow.overview.deadSpotHint')}</Text>}
                  </>
                )}
              </Stack>
            </Card>
          </Grid>
        </Section>

        <Section title={t('flow.corals.title')} description={survey.imageId ? `${String(t('flow.corals.description'))} ${String(t('flow.corals.shared'))}` : t('flow.corals.description')}>
          {survey.corals.length === 0 ? (
            <EmptyState
              headline={t('flow.corals.emptyTitle')}
              description={t('flow.corals.emptyDescription')}
              actionLabel={t('flow.corals.emptyAction')}
              actionVariant="secondary"
              onAction={() => startAdd('coral')}
              headingLevel={3}
            />
          ) : (
            <Card>
              <Stack gap="3">
                <Text weight="medium" data-testid="flow-coral-summary">
                  {t('flow.corals.summary', {
                    ok: counts.ok ?? 0,
                    weak: counts.too_weak ?? 0,
                    strong: counts.too_strong ?? 0,
                    open: (counts.uncertain ?? 0) + (counts.unknown_group ?? 0) + (counts.no_data ?? 0),
                  })}
                </Text>
                <FlowCoralList
                  corals={survey.corals}
                  assessments={assessment.corals}
                  points={survey.points}
                  busy={busy}
                  onEdit={(c) => startEdit({ kind: 'coral', id: c.id, x: c.x, y: c.y, zone: c.zone, livestockId: c.livestockId, label: c.label, careGroup: c.careGroup })}
                  onRemove={(c) => remove.mutate({ kind: 'corals', id: c.id })}
                />
                <Text color="muted" variant="caption">{t('flow.corals.source', { source: assessment.source })}</Text>
              </Stack>
            </Card>
          )}
        </Section>

        <Section title={t('flow.pumps.title')} description={t('flow.pumps.description')}>
          {survey.pumps.length === 0 ? (
            <EmptyState
              headline={t('flow.pumps.emptyTitle')}
              description={t('flow.pumps.emptyDescription')}
              actionLabel={t('flow.pumps.emptyAction')}
              actionVariant="secondary"
              onAction={() => startAdd('pump')}
              headingLevel={3}
            />
          ) : (
            <Card>
              <ul className="lighting__list" data-testid="flow-pumps">
                {survey.pumps.map((p) => {
                  const name = `P${p.sequence} ${p.label}`;
                  return (
                    <li key={p.id} className="lighting__row">
                      <Stack gap="1" className="lighting__row-text">
                        <Text weight="semibold">{name}</Text>
                        <Text color="secondary" size="sm">
                          {t(`flow.modes.${p.mode}`)}
                          {` · ${String(t('flow.pumps.programme', { min: p.minPercent, max: p.maxPercent }))}`}
                          {p.cycleSeconds !== null && ` · ${String(t('flow.pumps.cycle', { seconds: p.cycleSeconds }))}`}
                        </Text>
                        <Text size="sm">
                          {p.outlets
                            .map((o) => String(typeof o === 'number' ? t(`flow.directions.${o}`, { defaultValue: t('flow.pumps.degrees', { value: o }) }) : t(`flow.directions.${o}`)))
                            .join(' + ')}
                          {p.outlets.length > 1 && ` (${String(t('flow.pumps.twoOutlets'))})`}
                          {` · ${p.nominalFlowLph !== null ? String(t('flow.pumps.nominal', { value: p.nominalFlowLph })) : String(t('flow.pumps.nominalUnknown'))}`}
                        </Text>
                      </Stack>
                      <Inline gap="2">
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={busy}
                          aria-label={t('flow.pumps.editLabel', { name })}
                          onClick={() =>
                            startEdit({
                              kind: 'pump',
                              id: p.id,
                              x: p.x,
                              y: p.y,
                              label: p.label,
                              outlets: p.outlets,
                              nominalFlowLph: p.nominalFlowLph,
                              mode: p.mode,
                              minPercent: p.minPercent,
                              maxPercent: p.maxPercent,
                              cycleSeconds: p.cycleSeconds,
                            })
                          }
                        >
                          {t('flow.edit')}
                        </Button>
                        <Button size="sm" variant="ghost" disabled={busy} onClick={() => remove.mutate({ kind: 'pumps', id: p.id })} aria-label={t('flow.pumps.removeLabel', { name })}>
                          {t('flow.remove')}
                        </Button>
                      </Inline>
                    </li>
                  );
                })}
              </ul>
            </Card>
          )}
        </Section>

        <Section title={t('flow.points.title')}>
          {survey.points.length === 0 ? (
            <EmptyState
              headline={t('flow.points.emptyTitle')}
              description={t('flow.points.emptyDescription')}
              actionLabel={t('flow.points.emptyAction')}
              actionVariant="secondary"
              onAction={() => startAdd('point')}
              headingLevel={3}
            />
          ) : (
            <Card>
              <ul className="lighting__list" data-testid="flow-points">
                {survey.points.map((p) => {
                  const label = pointLabel(p.sequence);
                  const flag = flagOf.get(p.id) ?? null;
                  return (
                    <li key={p.id} className="lighting__row">
                      <Stack gap="1" className="lighting__row-text">
                        <Inline gap="2" align="center">
                          <Text weight="semibold">{t('flow.points.item', { label, range: formatRange(p.levelMin, p.levelMax) })}</Text>
                          {flag && <Badge tone={flag === 'dead_spot' || flag === 'blasting' ? 'warning' : 'info'}>{t(`flow.flags.${flag}`)}</Badge>}
                        </Inline>
                        <Text color="secondary" size="sm">
                          {t(`flow.methods.${p.method}`)}
                          {p.speedMinCms !== null && p.speedMaxCms !== null && ` · ${String(t('flow.points.speed', { range: formatRange(p.speedMinCms, p.speedMaxCms) }))}`}
                          {assessment.estimated && levelsOf.get(p.id) && ` · ${String(t('flow.points.estimatedAt', { range: formatRange(levelsOf.get(p.id)!.levelMin, levelsOf.get(p.id)!.levelMax), time: assessment.at ?? '' }))}`}
                          {` · ${String(t(p.alternating ? 'flow.corals.alternating' : 'flow.corals.oneDirection'))}`}
                          {` · ${p.zone ? String(t(`lighting.zones.${p.zone}`)) : String(t('lighting.zones.unknown'))}`}
                        </Text>
                      </Stack>
                      <Inline gap="2">
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={busy}
                          aria-label={t('flow.points.editLabel', { label })}
                          onClick={() =>
                            startEdit({
                              kind: 'point',
                              id: p.id,
                              x: p.x,
                              y: p.y,
                              zone: p.zone,
                              method: p.method,
                              levelMin: p.levelMin,
                              levelMax: p.levelMax,
                              speedMinCms: p.speedMinCms,
                              speedMaxCms: p.speedMaxCms,
                              alternating: p.alternating,
                            })
                          }
                        >
                          {t('flow.edit')}
                        </Button>
                        <Button size="sm" variant="ghost" disabled={busy} onClick={() => remove.mutate({ kind: 'points', id: p.id })} aria-label={t('flow.points.removeLabel', { label })}>
                          {t('flow.remove')}
                        </Button>
                      </Inline>
                    </li>
                  );
                })}
              </ul>
            </Card>
          )}
          {remove.isError && <Alert compact>{t('flow.editor.error')}</Alert>}
        </Section>

        <Section title={t('flow.evaluation.title')} description={t('flow.evaluation.description')}>
          <FlowEvaluationCard aquariumId={aquariumId} surveyId={surveyId} pointCount={survey.points.length} />
        </Section>

        <Section title={t('flow.finder.title')} description={t('flow.finder.description')}>
          <Card>
            <Stack gap="3">
              <Select
                id="flow-finder-group"
                label={t('flow.finder.group')}
                value={finderGroup}
                options={[
                  { value: '', label: String(t('flow.finder.choose')) },
                  ...(['soft', 'lps', 'sps', 'nps'] as const).map((g) => ({ value: g, label: String(t(`lighting.groups.${g}`)) })),
                ]}
                onChange={(e) => setFinderGroup(e.target.value as LightGroup | '')}
              />
              {spots && finderGroup && (
                <LiveRegion>
                  <Text data-testid="flow-finder-result">
                    {spots.pointIds.length === 0
                      ? t('flow.finder.none', { need: t(`flow.needs.${finderGroup}`) })
                      : t('flow.finder.result', {
                          need: t(`flow.needs.${finderGroup}`),
                          points: spots.pointIds
                            .map((id) => {
                              const p = survey.points.find((pt) => pt.id === id);
                              return p ? `${pointLabel(p.sequence)} (${formatRange(p.levelMin, p.levelMax)})` : '';
                            })
                            .filter(Boolean)
                            .join(', '),
                        })}
                  </Text>
                </LiveRegion>
              )}
            </Stack>
          </Card>
        </Section>

        <Section title={t('flow.details.title')}>
          <Card>
            <SurveyDetailsForm key={survey.updatedAt} survey={survey} onSaved={(s) => applied(s, String(t('flow.details.saved')))} onDelete={() => setConfirmDelete(true)} />
          </Card>
        </Section>

        <Dialog isOpen={copyAt !== null} onClose={() => setCopyAt(null)} title={t('flow.copy.title')} size="md" data-testid="flow-copy-dialog">
          {copyAt !== null && (
            <CopySurveyForm
              survey={survey}
              clock={copyAt.clock}
              onCancel={() => setCopyAt(null)}
              onCreated={(created) => {
                setCopyAt(null);
                void queryClient.invalidateQueries({ queryKey: flowSurveysKey(aquariumId) });
                setAt(null);
                navigate(`/aquariums/${encodeURIComponent(aquariumId)}/flow/${encodeURIComponent(created.id)}`);
              }}
            />
          )}
        </Dialog>

        <Dialog
          isOpen={confirmDelete}
          onClose={() => setConfirmDelete(false)}
          title={t('flow.details.confirmTitle')}
          size="sm"
          footer={
            <Inline gap="2" justify="end">
              <Button variant="ghost" onClick={() => setConfirmDelete(false)}>{t('flow.cancel')}</Button>
              <Button variant="danger" isLoading={removeSurvey.isPending} onClick={() => removeSurvey.mutate()}>{t('flow.details.delete')}</Button>
            </Inline>
          }
        >
          <Stack gap="3">
            <Text>{t('flow.details.confirmText')}</Text>
            {removeSurvey.isError && <Alert compact>{t('flow.details.deleteError')}</Alert>}
          </Stack>
        </Dialog>
      </>
    );
  }

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="default">
          <PageHeader
            back={backLink}
            title={survey?.title ?? t('flow.detail.title')}
            description={survey ? `${formatSurveyDate(survey.measuredAt, i18n.language || 'de')}${survey.measuredClock ? ` · ${survey.measuredClock}` : ''}` : undefined}
          />
          {survey && survey.linkedSurveys.length > 0 && (
            <Inline gap="2" align="center" data-testid="flow-linked-surveys">
              <Text size="sm" color="secondary">{t('flow.linked.label')}</Text>
              {survey.linkedSurveys.map((l) => (
                <Link key={l.id} className="lighting__back-link" to={`/aquariums/${encodeURIComponent(aquariumId)}/flow/${encodeURIComponent(l.id)}`} onClick={() => setAt(null)}>
                  {l.measuredClock ? `${l.title} (${l.measuredClock})` : l.title}
                </Link>
              ))}
            </Inline>
          )}
          <LiveRegion visuallyHidden>{announcement}</LiveRegion>
          {content}
        </PageContent>
      }
    />
  );
}

export default FlowSurveyPage;
